<template>
  <v-dialog
      :value="value"
      content-class="acq-dialog"
      max-width="500px"
      @click:outside="$emit('input', false)"
      @keydown.esc="$emit('input', false)"
      :overlay="false"
  >
    <v-card class="acq-dialog mdlg-scope">
      <div class="mdlg-header">
        <profile-avatar :size="40" :user="responsible"/>
        <div class="mdlg-header__text">
          <div class="acq-section-title text-truncate">
            {{ toSalutation(responsible.gender) }}
            <span v-can:read-responsibles="{ id: responsible._id, keys: ['firstName'] }">{{ responsible.firstName }}</span>
            {{ responsible.lastName }}
          </div>
          <div class="acq-tiny text-truncate">
            <span v-can:read-responsibles="{ id: responsible._id, keys: ['jobType'] }">{{ getJobTypeString(responsible) }}</span>
          </div>
        </div>
        <v-btn aria-label="Schließen" icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-list class="py-0" dense>
        <contact-data-list-items :entity="responsible" v-can:read-responsibles="{ id: responsible._id, keys: ['contactInformation'] }"/>
        <v-divider/>

        <!-- Birthday -->
        <v-list-item v-can:read-responsibles="{ id: responsible._id, keys: ['birthday'] }">
          <v-list-item-avatar>
            <v-icon>mdi-cake-variant-outline</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>
              {{ responsible.birthday ? moment(responsible.birthday).format('DD.MM.YYYY') : 'Geburtsdatum unbekannt' }}
            </v-list-item-title>
            <v-list-item-subtitle>Geburtstag</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <div class="mdlg-footer" v-can:update-institutions="{keys: ['responsibles'], id: this.institutionId}">
        <v-tooltip right v-can:custom-shareAccess>
          <template v-slot:activator="{on}">
            <button class="cta cta--quiet" type="button" @click="shareAccess" v-on="on">
              <v-icon class="cta__icon" small>mdi-shield-lock-open-outline</v-icon>Freigeben
            </button>
          </template>
          Ansprechpartner:in freigeben
        </v-tooltip>
        <v-spacer/>
        <button class="cta cta--danger" type="button" v-can:delete-responsibles="{ id: this.responsible._id }" @click="showDeleteAlert = true">
          <v-icon class="cta__icon" small>mdi-delete-outline</v-icon>Löschen
        </button>
      </div>
    </v-card>

    <v-dialog v-model="showDeleteAlert" content-class="acq-dialog" max-width="420px" persistent>
      <v-card class="acq-dialog mdlg-scope">
        <div class="d-flex align-start pa-5 pb-3" style="gap: 12px">
          <span class="acq-avatar acq-avatar--sm tone-bad"><v-icon small style="color: inherit">mdi-delete-outline</v-icon></span>
          <div class="flex-grow-1" style="min-width: 0">
            <div class="acq-section-title">Ansprechpartner:in löschen?</div>
            <div class="acq-tiny mt-1">
              {{ [responsible.firstName, responsible.lastName].filter(Boolean).join(' ') }} wird gelöscht und vom Standort entfernt.
              Dies kann nicht rückgängig gemacht werden.
            </div>
          </div>
        </div>
        <div class="d-flex align-center pa-5 pt-2" style="gap: 8px">
          <v-spacer/>
          <button :disabled="loading" class="cta cta--ghost" type="button" @click="showDeleteAlert = false">Abbrechen</button>
          <button :disabled="loading" class="cta cta--danger" type="button" v-can:delete-responsibles="{ id: responsible._id }"
                  @click="deleteResponsible">
            <v-progress-circular v-if="loading" indeterminate size="14" width="2"/>
            <v-icon v-else class="cta__icon" small>mdi-delete-outline</v-icon>Löschen
          </button>
        </div>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>


<script lang="ts">
import Vue from 'vue';
import { Responsible } from '@/interfaces/responsible.interface';
import { ResponsibleHandlerAPI } from '@/classes/api/responsible-handler.api.class';
import ProfileAvatar from '@/components/common/profile-avatar.vue';
import ContactDataListItems from '@/components/common/profile/contactDataListItems.vue';
import { toSalutation } from '@/helpers/toSalutation.helper';
import moment from 'moment';
import { CrudEntityTypes } from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import { isJobType } from '@/interfaces/jobType.interface';
import { GenderTypes } from '@/enums/GenderTypes.enum';
import { EventBus } from '@/busses/EventBus';
import mongoose from 'mongoose';

export default Vue.extend({
  components: { ProfileAvatar, ContactDataListItems },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    responsible: {
      type: Object as () => Responsible,
      required: true,
    },
    institutionId: {
      type: Object as () => mongoose.Types.ObjectId,
      required: true,
    },
  },
  data: () => ({
    showDeleteAlert: false,
    loading: false,
  }),
  computed: {
    toSalutation: () => toSalutation,
    moment: () => moment,
  },
  methods: {
    getJobTypeString(responsible: Responsible) {
      if (responsible.jobType && isJobType(responsible.jobType)) {
        switch (responsible.gender) {
          case GenderTypes.FEMALE:
            return responsible.jobType.female;
          case GenderTypes.MALE:
            return responsible.jobType.male;
          case GenderTypes.TRANS:
            return responsible.jobType.neutral;
          default:
            return responsible.jobType.neutral;
        }
      } else {
        return 'Jobposition unbekannt';
      }
    },
    shareAccess() {
      this.$$shareAccessDialog.show(CrudEntityTypes.RESPONSIBLE, this.responsible._id, 'Ansprechpartner:in freigeben')
    },
    async deleteResponsible() {
      try {
        this.loading = true;
        await ResponsibleHandlerAPI.remove(this.responsible._id, {
          pullFromInstitution: this.institutionId,
        });
        this.showDeleteAlert = false;
        this.$emit('delete:responsible');
        EventBus.$emit('delete:responsible');
        this.$emit('input', false);
        this.$$showSnackbar('Ansprechpartner:in wurde erfolgreich gelöscht.', 'success')
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
